import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import {
  IconLayoutDashboard,
  IconTruck,
  IconClipboardList,
  IconUsers,
  IconAlertTriangle,
  IconDroplet,
  IconSettings,
  IconBuilding,
  IconShieldCheck,
  IconLogout,
} from '@tabler/icons-react';

const navItems = {
  super_admin: [
    { icon: IconBuilding, label: 'Overview', path: '/group' },
    { icon: IconTruck, label: 'Assets', path: '/assets' },
    { icon: IconClipboardList, label: 'Checks', path: '/checks' },
    { icon: IconUsers, label: 'Staff', path: '/staff' },
    { icon: IconAlertTriangle, label: 'Incidents', path: '/incidents' },
    { icon: IconDroplet, label: 'Fuel', path: '/fuel' },
  ],
  senior_manager: [
    { icon: IconBuilding, label: 'Overview', path: '/group' },
    { icon: IconTruck, label: 'Assets', path: '/assets' },
    { icon: IconClipboardList, label: 'Checks', path: '/checks' },
    { icon: IconUsers, label: 'Staff', path: '/staff' },
    { icon: IconAlertTriangle, label: 'Incidents', path: '/incidents' },
    { icon: IconDroplet, label: 'Fuel', path: '/fuel' },
  ],
  manager: [
    { icon: IconLayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: IconTruck, label: 'Assets', path: '/assets' },
    { icon: IconClipboardList, label: 'Checks', path: '/checks' },
    { icon: IconUsers, label: 'Staff', path: '/staff' },
    { icon: IconAlertTriangle, label: 'Incidents', path: '/incidents' },
    { icon: IconDroplet, label: 'Fuel', path: '/fuel' },
  ],
  fuel_manager: [
    { icon: IconLayoutDashboard, label: 'Dashboard', path: '/fuel-dashboard' },
    { icon: IconDroplet, label: 'Fuel', path: '/fuel' },
    { icon: IconClipboardList, label: 'Checks', path: '/checks' },
  ],
};

export default function Layout({ children }) {
  const { userProfile, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const items = navItems[role] || [];
  const isProfileActive = location.pathname.startsWith('/profile');

  const getDashHome = () => {
    if (role === 'fuel_manager') return '/fuel-dashboard';
    if (role === 'senior_manager' || role === 'super_admin') return '/group';
    return '/dashboard';
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const initials = userProfile?.name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  const photoURL = userProfile?.photoURL || null;

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      background: 'var(--color-bg-tertiary)',
    }}>

      {/* Sidebar */}
      <div style={{
        width: 'var(--sidebar-width)',
        background: 'var(--color-bg-primary)',
        borderRight: '0.5px solid var(--color-border-light)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '14px 0',
        gap: 4,
        flexShrink: 0,
        zIndex: 10,
      }}>

        {/* Logo */}
        <div
          onClick={() => navigate(getDashHome())}
          style={{
            width: 32,
            height: 32,
            background: 'var(--color-brand)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 10,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <IconShieldCheck size={18} color="white" stroke={1.5} />
        </div>

        {/* Nav items */}
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          return (
            <div
              key={item.path}
              onClick={() => navigate(item.path)}
              title={item.label}
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: isActive ? 'var(--color-brand-light)' : 'transparent',
                color: isActive ? 'var(--color-brand)' : 'var(--color-text-tertiary)',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <Icon size={18} stroke={1.5} />
            </div>
          );
        })}

        {/* Bottom items */}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>

          {/* Settings */}
          {(role === 'super_admin' || role === 'senior_manager') && (
            <div
              onClick={() => navigate('/settings/branches')}
              title="Settings"
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: location.pathname.startsWith('/settings') ? 'var(--color-brand-light)' : 'transparent',
                color: location.pathname.startsWith('/settings') ? 'var(--color-brand)' : 'var(--color-text-tertiary)',
              }}
            >
              <IconSettings size={18} stroke={1.5} />
            </div>
          )}

          {/* Profile avatar */}
          <div
            onClick={() => navigate('/profile')}
            title="Profile"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
              flexShrink: 0,
              border: isProfileActive ? '2px solid var(--color-brand)' : '2px solid transparent',
              transition: 'border-color 0.15s',
              background: photoURL ? 'transparent' : isProfileActive ? 'var(--color-brand-light)' : 'var(--color-bg-secondary)',
              backgroundImage: photoURL ? `url(${photoURL})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              color: isProfileActive ? 'var(--color-brand)' : 'var(--color-text-tertiary)',
              fontSize: 11,
            }}
          >
            {!photoURL && initials}
          </div>

          {/* Sign out */}
          <div
            onClick={handleSignOut}
            title="Sign out"
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--color-text-tertiary)',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--color-danger)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--color-text-tertiary)'}
          >
            <IconLogout size={18} stroke={1.5} />
          </div>

        </div>
      </div>

      {/* Main content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        minWidth: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {children}
      </div>

    </div>
  );
}